import styled from '@emotion/styled';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useAttachmentUrl } from '../hooks/useAttachmentUrl';
import type { ChatAttachment } from '../models/attachment';
import { attachmentTypeLabel, isImageMimeType } from '../services/attachment';
import {
  colorAgentBorder,
  colorAgentTextPrimary,
  colorAgentTextSecondary,
  colorNeutralWhite,
  fontSizeXs,
  space2,
} from '../styles/tokens';

const THUMBNAIL_SIZE = '92px';

const Gallery = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${space2};
`;

const Thumbnail = styled(Box)`
  width: ${THUMBNAIL_SIZE};
  height: ${THUMBNAIL_SIZE};
  border: 1px solid ${colorAgentBorder};
  border-radius: 8px;
  background-color: ${colorNeutralWhite};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThumbnailLink = styled.a`
  display: block;
  width: 100%;
  height: 100%;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  /* screenshot を切らずに全体を見せる */
  object-fit: contain;
  display: block;
`;

const FileNameFallback = styled(Typography)`
  font-size: ${fontSizeXs};
  color: ${colorAgentTextSecondary};
  padding: 0 4px;
  text-align: center;
  overflow-wrap: anywhere;
`;

const FileCard = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 200px;
  padding: 8px 10px;
  border: 1px solid ${colorAgentBorder};
  border-radius: 8px;
  background-color: ${colorNeutralWhite};
  text-decoration: none;
`;

const FileCardIcon = styled.span`
  font-size: 20px;
  color: ${colorAgentTextSecondary};
  display: flex;
  flex-shrink: 0;
`;

const FileCardText = styled(Box)`
  min-width: 0;
`;

const FileCardName = styled(Typography)`
  font-size: ${fontSizeXs};
  color: ${colorAgentTextPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileCardType = styled(Typography)`
  font-size: 10px;
  color: ${colorAgentTextSecondary};
`;

function UserAttachmentThumbnail({ attachment }: { attachment: ChatAttachment }) {
  const { url, hasFailed } = useAttachmentUrl(attachment.key);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);

  if (hasFailed || hasLoadFailed) {
    return (
      <Thumbnail>
        <FileNameFallback>{attachment.fileName}</FileNameFallback>
      </Thumbnail>
    );
  }

  if (!url) {
    return <Thumbnail aria-busy="true" />;
  }

  return (
    <Thumbnail>
      <ThumbnailLink href={url} target="_blank" rel="noopener noreferrer">
        <ThumbnailImage
          src={url}
          alt={attachment.fileName}
          loading="lazy"
          onError={() => setHasLoadFailed(true)}
        />
      </ThumbnailLink>
    </Thumbnail>
  );
}

function UserAttachmentCard({ attachment }: { attachment: ChatAttachment }) {
  const { url } = useAttachmentUrl(attachment.key);
  const card = (
    <FileCard>
      <FileCardIcon as={PictureAsPdfOutlinedIcon} />
      <FileCardText>
        <FileCardName>{attachment.fileName}</FileCardName>
        <FileCardType>{attachmentTypeLabel(attachment.mimeType)}</FileCardType>
      </FileCardText>
    </FileCard>
  );

  if (!url) {
    return card;
  }
  return (
    <Box component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none' }}>
      {card}
    </Box>
  );
}

export function UserMessageAttachments({ attachments }: { attachments: readonly ChatAttachment[] }) {
  if (attachments.length === 0) {
    return null;
  }

  return (
    <Gallery>
      {attachments.map((attachment) =>
        isImageMimeType(attachment.mimeType) ? (
          <UserAttachmentThumbnail key={attachment.key} attachment={attachment} />
        ) : (
          <UserAttachmentCard key={attachment.key} attachment={attachment} />
        ),
      )}
    </Gallery>
  );
}
